<template>
  <Loader
    v-if="$attrs.show"
    :value="promises.fetch"
    :message="$t('loading', { resource: $tc('unitCatalog') }) + '...'"
  >
    <ModalComponent class="residential-create-modal" v-bind="$attrs" v-on="$listeners">
      <Card tag="form" theme="flat" @submit.prevent="submit">
        <template #default>
          <strong>{{ $t('createUnit') }}</strong>
          <hr class="mb-2" />
          <div class="residential-create-modal__inputs">
            <ResidentialNumber
              v-model.number="data.number"
              data-field="number"
              :pattern="numberPattern"
              :is-valid="validator.fields.number.valid"
              :validation-message="validator.fields.number.error"
            />
            <InputComponent
              v-model.number="data.quantity"
              type="number"
              required
              data-field="quantity"
              :label="labels.quantity"
              :is-valid="validator.fields.quantity.valid"
              :validation-message="validator.fields.quantity.error"
            />
            <InputComponent data-field="saleType" :label="labels.saleType">
              <Select v-model="data.sale.type" :placeholder="`${$t('select')}...`">
                <option v-for="item in enums.ResidentialSaleType" :key="item" :value="item">
                  {{ $t(item) }}
                </option>
              </Select>
            </InputComponent>

            <InputComponent data-field="residentialTemplate" :label="labels.template">
              <Select v-model="data.template">
                <option :value="null">{{ $t('none') }}</option>
                <option v-for="item in templates" :key="item.id" :value="item.id">{{ item.internalName }}</option>
              </Select>
              <template #below>
                <Alert v-if="$store.getters['hasAccess']('residentialTemplates')" class="mt-1">
                  {{ $t('unitCreateCatalogAlert') }}
                  <router-link
                    style="display: block"
                    class="mt-1"
                    :to="{ name: 'residential-template-create', query: { returnTo: $route.path } }"
                    >{{ $t('createUnitCatalog') }}</router-link
                  >
                </Alert>
              </template>
            </InputComponent>
          </div>
        </template>
        <template #footer>
          <Alert v-if="limitReached" theme="error" class="residential-create-modal__limit-alert">{{
            $t('projectUnitLimitReached', [remainingUnits])
          }}</Alert>
          <ButtonComponent
            type="submit"
            :icon="['fal', 'plus']"
            :promise="promises.mutate"
            @onSuccess="$emit('close')"
            :label="$t('create')"
            :disabled="!validator.passes || limitReached"
            theme="primary"
          />
          <ButtonComponent
            :label="$tc('import')"
            tag="router-link"
            :to="{ name: 'import', params: { type: 'unit' } }"
            :icon="['fal', 'plus']"
            :disabled="limitReached"
            theme="secondary"
          />
          <ButtonComponent :label="$t('cancel')" @click="$emit('close')" />
        </template>
      </Card>
    </ModalComponent>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator'
import ResidentialTemplateAPI from '@/api/ResidentialTemplate'
import ResidentialAPI from '@/api/Residential'
import { mapi18n } from '@/i18n'
import API from '@/api/Residential'
import { mapState } from 'vuex'

import Card from '@/components/Card'
import { ModalComponent } from 'vue-elder-modal'
import { InputComponent } from 'vue-elder-input'
import { AlertComponent as Alert } from 'vue-elder-alert'
import ResidentialNumber from '@/components/Residential/Fields/Number'
import Select from '@/components/Select/Select'

export default {
  mixins: [Validator],
  props: {
    project: String,
    numberPattern: String,
  },
  watch: {
    numberSuggestion: {
      handler(val) {
        this.data.number = val
      },
      immediate: true,
    },
    '$attrs.show': {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      data: {
        number: null,
        quantity: 1,
        template: null,
        sale: {
          type: 'sale',
        },
      },
      numberSuggestion: 1,
      remainingUnits: 0,
      templates: [],
      promises: {
        fetch: null,
        mutate: null,
      },
      validator: {
        rules: 'rules',
        labels: 'labels',
        data: 'data',
      },
    }
  },
  computed: {
    ...mapState(['enums']),
    ...mapi18n('labels', {
      number: 'unitNumber',
      quantity: 'quantity',
      template: 'unitCatalog',
      saleType: 'saleType',
    }),
    limitReached() {
      return this.data.quantity > this.remainingUnits
    },
    rules(){
      return {
          number: `residentialNumber:${this.project},${this.numberSuggestion}|required|min:1`,
          quantity: 'required|min:1|max:200',
        }
    }
  },
  methods: {
    submit() {
      let data = { ...this.data, project: this.project }
      this.promises.mutate = ResidentialAPI.mutate(data).then(res => this.$emit('input', res))
    },
    fetch() {
      this.promises.fetch = Promise.all([
        ResidentialTemplateAPI.get().then(data => (this.templates = data)),
        API.get({ id: this.project }, { query: 'ResidentialNumberSuggestion' }).then(data => {
          this.numberSuggestion = data.metadata.residentialNumberSuggestion
          this.remainingUnits = data.metadata.remainingUnits
        }),
      ])
    },
  },
  components: {
    Card,
    ModalComponent,
    InputComponent,
    ResidentialNumber,
    Alert,
    Select,
  },
}
</script>

<style lang="scss">
.residential-create-modal {
  .card {
    width: 100%;
    max-width: 500px;
  }

  &__limit-alert {
    width: 100%;
  }

  &__inputs {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1rem;
    align-items: start;

    [data-field='residentialTemplate'] {
      grid-column-end: span 2;
    }
    [data-field='saleType'] {
      grid-column-end: span 2;
    }
  }
}
</style>
